import { Redirect, router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H2Emphasized,
    Icon,
    InfoRow,
    isNonMobileWeb,
    LockIcon,
    RowList,
    ScreenContainer,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { captureException } from '../../../logger/Logger';
import useUpdateCreditCard from '../../hooks/card/useUpdateCreditCard';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import {
    type Card,
    CardStatus,
    UpdateReason,
    UpdateStatus,
} from '../../types/Card';
import FruitfulCard from './FruitfulCard';

enum LockCardSteps {
    ABOUT = 'about',
    CONFIRM_LOCK = 'confirmLock',
}

const CARD_PAYMENT_WARNING =
    'Transactions that were authorized before the card is locked may continue, including recurring transactions, subscriptions, and scheduled payments. Payments may continue but Fruitful is not responsible if a payment cannot be completed while the Fruitful Card is in a locked state.';

const LockCard = () => {
    const { syncteraAccountId, cardId } = useLocalSearchParams<{
        syncteraAccountId: string;
        cardId: string;
    }>();

    const [index, setIndex] = useState(0);

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    const toast = useToastController();

    const { mutateAsync: updateCreditCard, isPending: isUpdatingCard } =
        useUpdateCreditCard();
    const { refetchAccountDetails, isLoading } =
        useGetAllBankAccountDetails(false);

    if (syncteraAccountId == null) {
        captureException(new Error('Account id not found for lock card page'), {
            cardId,
        });
        return <Redirect href="/home/banking" />;
    }

    if (account == null || isUpdatingCard || isLoading) {
        return <FullScreenSpinner />;
    }

    const card = account.cards?.find((card) => card.id === cardId);

    if (card == null) {
        captureException(new Error('Card not found for lock card page'));
        return <Redirect href="/home/banking" />;
    }

    const routes = Object.values(LockCardSteps).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (lockStep: LockCardSteps) =>
        routes.findIndex(({ key }) => key === lockStep);

    const aboutLockingIndex = getRouteIndex(LockCardSteps.ABOUT);
    const confirmLockIndex = getRouteIndex(LockCardSteps.CONFIRM_LOCK);

    const getTabTitle = ({
        index,
        cardStatus,
    }: {
        index: number;
        cardStatus: CardStatus;
    }) => {
        switch (index) {
            case aboutLockingIndex:
                return 'Lock card';
            case confirmLockIndex:
                return cardStatus === CardStatus.SUSPENDED
                    ? 'Card locked'
                    : 'Lock your card';
            default:
                return '';
        }
    };

    const onPressBack = () => {
        if (index !== aboutLockingIndex) {
            setIndex(index - 1);
        } else {
            router.back();
        }
    };

    const onUpdateCreditCard = async () => {
        try {
            await updateCreditCard({
                urlParams: { cardId },
                body: {
                    cardStatus: UpdateStatus.SUSPENDED,
                    reason: UpdateReason.REQ,
                },
            });
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong.');
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: LockCardSteps } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const Content = (() => {
            switch (route.key) {
                case LockCardSteps.ABOUT:
                    return (
                        <About
                            onPress={() => {
                                setIndex(confirmLockIndex);
                            }}
                        />
                    );
                case LockCardSteps.CONFIRM_LOCK:
                    return (
                        <ConfirmLock
                            onPress={async () => {
                                if (card.cardStatus === CardStatus.SUSPENDED) {
                                    router.navigate({
                                        pathname: '/banking/card/manage-card',
                                        params: {
                                            syncteraAccountId,
                                            cardId: card.id,
                                        },
                                    });
                                } else {
                                    await onUpdateCreditCard();
                                }
                            }}
                            card={card}
                        />
                    );
                default:
                    return <></>;
            }
        })();

        return (
            <YStack
                flex={1}
                maxHeight={isNonMobileWeb ? '70vh' : '100%'}
                width="100%"
                maxWidth="100%"
                $sm={{
                    width: 500,
                    alignSelf: 'center',
                    marginTop: 50,
                }}
            >
                {Content}
            </YStack>
        );
    };

    return (
        <ScreenContainer
            hasBackButton={true}
            onBack={onPressBack}
            title={getTabTitle({
                index,
                cardStatus: card.cardStatus,
            })}
        >
            <View height="100%">
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    onIndexChange={setIndex}
                    renderScene={renderScene}
                    renderTabBar={() => <></>}
                />
            </View>
        </ScreenContainer>
    );
};

const About = ({ onPress }: { onPress: () => void }) => {
    return (
        <YStack flex={1} justifyContent="space-between">
            <YStack alignItems="center" gap={16}>
                <View
                    borderRadius={20}
                    backgroundColor="$gray100"
                    width={90}
                    height={90}
                    padding={20}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Icon icon={LockIcon} size={40} color="$black" />
                </View>
                <H2Emphasized marginTop={16} textAlign="center">
                    Locking your card
                </H2Emphasized>
                <BodyMediumRegular
                    color="$gray1000"
                    textAlign="center"
                    marginTop={8}
                >
                    This protects against unauthorized purchases.
                </BodyMediumRegular>
                <YStack gap={16}>
                    <BodySmallRegular color="$gray1000" textAlign="center">
                        When you lock your Fruitful Card, new charges—like
                        in-store, online, in-app, and phone purchases—won&apos;t
                        go through. However, ongoing payments, like
                        subscriptions and scheduled transactions, will still
                        process.
                    </BodySmallRegular>
                    <BodySmallRegular color="$gray1000" textAlign="center">
                        Remember to keep an eye on your account and make
                        payments, even when your card is locked.
                    </BodySmallRegular>
                </YStack>
            </YStack>
            <Button
                onPress={onPress}
                size={ButtonSize.LARGE}
                text="Continue"
                variant={ButtonVariant.PRIMARY}
            />
        </YStack>
    );
};

interface ConfirmLockProps {
    card: Card;
    onPress: () => void;
}

const ConfirmLock = ({ card, onPress }: ConfirmLockProps) => {
    const {
        embossName,
        form,
        color,
        cardStatus,
        lastFour,
        userCardStatusReason,
    } = card;

    const isCardLocked = cardStatus === CardStatus.SUSPENDED;

    return (
        <YStack flex={1} justifyContent="space-between">
            <YStack>
                <FruitfulCard
                    cardholderName={embossName.line1}
                    cardStatus={cardStatus}
                    userCardStatusReason={userCardStatusReason}
                    form={form}
                    color={color}
                    width={316}
                    alignSelf="center"
                />
                {isCardLocked && (
                    <H2Emphasized textAlign="center" marginVertical={20}>
                        Your card is now locked
                    </H2Emphasized>
                )}
                <RowList>
                    <InfoRow title="Card holder" value={embossName.line1} />
                    <InfoRow
                        title="Card number"
                        value={`*${lastFour}`}
                        isPrivate
                    />
                </RowList>
                <BodySmallRegular color="$gray1000" marginTop={20}>
                    {CARD_PAYMENT_WARNING}
                </BodySmallRegular>
            </YStack>

            <Button
                onPress={onPress}
                size={ButtonSize.LARGE}
                tone={isCardLocked ? 'neutral' : 'danger'}
                variant={ButtonVariant.PRIMARY}
                text={isCardLocked ? 'Done' : 'Lock card'}
            />
        </YStack>
    );
};

export default LockCard;
